import React, { useState, useEffect } from 'react';
import {
  Box,
  CssBaseline,
  ThemeProvider,
  Typography,
  createTheme,
  IconButton,
  Fade,
  Paper,
  Stack,
  Button,
  useMediaQuery,
  alpha,
  Container,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InsightsIcon from '@mui/icons-material/Insights';
import SettingsIcon from '@mui/icons-material/Settings';
import MessageIcon from '@mui/icons-material/Message';
import CombinedScheduleForm from './components/CombinedScheduleForm';
import ScheduledCallsList from './components/ScheduledCallsList';
import CallAnalytics from './components/CallAnalytics';
import Login from './components/Login';
import { authService } from './services/auth';
import ReminderSettings from './components/ReminderSettings';
import { reminderService } from './services/reminderService';
import ScheduledMessagesList from './components/ScheduledMessagesList';
import YobiAIWidget from './components/YobiAIWidget';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1e293b',
    },
    secondary: {
      main: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: alpha('#000000', 0.7),
    },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.7,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      letterSpacing: '-0.01em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30,
          padding: '10px 24px',
          fontSize: '0.95rem',
        },
        contained: {
          backgroundColor: '#1e293b',
          color: '#fff',
          '&:hover': {
            backgroundColor: alpha('#1e293b', 0.9),
          },
        },
        outlined: {
          borderColor: '#1e293b',
          color: '#1e293b',
          '&:hover': {
            backgroundColor: alpha('#1e293b', 0.04),
            borderColor: '#1e293b',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          border: '1px solid',
          borderColor: alpha('#000', 0.08),
        },
      },
    },
  },
});

function App() {
  const [view, setView] = useState<'schedule' | 'list' | 'analytics' | 'reminders' | 'messages'>(() => {
    // Initialize view from URL path
    const path = window.location.pathname.substring(1);
    if (['schedule', 'list', 'analytics', 'reminders', 'messages'].includes(path)) {
      return path as 'schedule' | 'list' | 'analytics' | 'reminders' | 'messages';
    }
    return 'schedule'; // Default to schedule view
  });

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasReminderAgent, setHasReminderAgent] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const checkReminderAgent = async () => {
      try {
        const response = await reminderService.getReminderSettings();
        setHasReminderAgent(!!response.results.reminder_agent);
        // If we're on the reminders view but there's no agent, redirect to schedule
        if (view === 'reminders' && !response.results.reminder_agent) {
          setView('schedule');
        }
      } catch (err) {
        console.error('Failed to check reminder agent:', err);
        setHasReminderAgent(false);
      }
    };

    if (isAuthenticated) {
      checkReminderAgent();
    }
  }, [isAuthenticated]);

  // Handle browser navigation
  useEffect(() => {
    const handleLocationChange = () => {
      const path = window.location.pathname.substring(1);
      if (['schedule', 'list', 'analytics', 'reminders', 'messages'].includes(path)) {
        setView(path as 'schedule' | 'list' | 'analytics' | 'reminders' | 'messages');
      } else {
        setView('schedule');
      }
    };

    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  // Update URL when view changes
  useEffect(() => {
    const newPath = view === 'schedule' ? '/' : `/${view}`;
    if (window.location.pathname !== newPath) {
      window.history.pushState({}, '', newPath);
    }
  }, [view]);

  useEffect(() => {
    // Check URL parameters
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    const urlRefreshToken = params.get('refreshToken');
    const sourceUrl = params.get('source');

    // Update source URL in localStorage if provided
    if (sourceUrl) {
      localStorage.setItem('source_url', sourceUrl);
      // Clean up source from URL
      params.delete('source');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    }

    // Check for authentication status (this will handle both URL tokens)
    const isAuth = authService.isAuthenticated();
    setIsAuthenticated(isAuth);
    
    // If not authenticated and no tokens in URL, redirect to login
    if (!isAuth && !urlToken && !urlRefreshToken) {
      console.log('No valid authentication found');
    }
  }, []);

  const handleBackToYobi = () => {
    const sourceUrl = localStorage.getItem('source_url') || 'https://app.yib.io';
    window.location.href = sourceUrl;
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
          }}
        >
          <Login onLoginSuccess={handleLoginSuccess} />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <CssBaseline />
        {!isAuthenticated ? (
          <Login onLoginSuccess={handleLoginSuccess} />
        ) : (
          <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            {/* Navigation Bar */}
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: 64,
                px: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: '#1e293b',
                color: 'white',
                zIndex: 1000,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackToYobi}
                  sx={{
                    color: 'white',
                    '&:hover': { backgroundColor: alpha('#fff', 0.1) },
                  }}
                >
                  Back to Yobi
                </Button>
              </Stack>

              <Stack direction="row" spacing={2} alignItems="center">
                <Stack direction="row" spacing={1}>
                  <Button
                    onClick={() => setView('schedule')}
                    sx={{
                      color: 'white',
                      opacity: view === 'schedule' ? 1 : 0.7,
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: alpha('#fff', 0.1),
                      },
                      borderBottom: view === 'schedule' ? '2px solid white' : 'none',
                      borderRadius: 0,
                      px: 2,
                    }}
                  >
                    Schedule Calls
                  </Button>
                  <Button
                    onClick={() => setView('list')}
                    sx={{
                      color: 'white',
                      opacity: view === 'list' ? 1 : 0.7,
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: alpha('#fff', 0.1),
                      },
                      borderBottom: view === 'list' ? '2px solid white' : 'none',
                      borderRadius: 0,
                      px: 2,
                    }}
                  >
                    View Calls
                  </Button>
                  <Button
                    onClick={() => setView('analytics')}
                    sx={{
                      color: 'white',
                      opacity: view === 'analytics' ? 1 : 0.7,
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: alpha('#fff', 0.1),
                      },
                      borderBottom: view === 'analytics' ? '2px solid white' : 'none',
                      borderRadius: 0,
                      px: 2,
                    }}
                  >
                    Analytics
                  </Button>
                  <Button
                    onClick={() => setView('messages')}
                    sx={{
                      color: 'white',
                      opacity: view === 'messages' ? 1 : 0.7,
                      '&:hover': {
                        opacity: 1,
                        backgroundColor: alpha('#fff', 0.1),
                      },
                      borderBottom: view === 'messages' ? '2px solid white' : 'none',
                      borderRadius: 0,
                      px: 2,
                    }}
                  >
                    View Messages
                  </Button>
                  {hasReminderAgent && (
                    <Button
                      onClick={() => setView('reminders')}
                      sx={{
                        color: 'white',
                        opacity: view === 'reminders' ? 1 : 0.7,
                        '&:hover': {
                          opacity: 1,
                          backgroundColor: alpha('#fff', 0.1),
                        },
                        borderBottom: view === 'reminders' ? '2px solid white' : 'none',
                        borderRadius: 0,
                        px: 2,
                      }}
                    >
                      Reminders
                    </Button>
                  )}
                </Stack>
                
                <IconButton
                  onClick={handleLogout}
                  sx={{
                    color: 'white',
                    '&:hover': { backgroundColor: alpha('#fff', 0.1) },
                    ml: 2,
                  }}
                >
                  <LogoutIcon />
                </IconButton>
              </Stack>
            </Box>

            {/* Main Content */}
            <Container maxWidth="lg" sx={{ pt: 10, pb: 4 }}>
              <Fade in={true} timeout={450}>
                <Box>
                  {view === 'schedule' && (
                    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                      <Typography variant="h2" gutterBottom>Schedule Calls</Typography>
                      <CombinedScheduleForm onSuccess={() => {}} />
                    </Paper>
                  )}
                  {view === 'list' && (
                    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                      <Typography variant="h2" gutterBottom>Scheduled Calls</Typography>
                      <ScheduledCallsList />
                    </Paper>
                  )}
                  {view === 'analytics' && (
                    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                      <Typography variant="h2" gutterBottom>Call Analytics</Typography>
                      <CallAnalytics />
                    </Paper>
                  )}
                  {view === 'messages' && (
                    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                      <Typography variant="h2" gutterBottom>Scheduled Messages</Typography>
                      <ScheduledMessagesList />
                    </Paper>
                  )}
                  {view === 'reminders' && (
                    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                      <Typography variant="h2" gutterBottom>Reminder Settings</Typography>
                      <ReminderSettings />
                    </Paper>
                  )}
                </Box>
              </Fade>
            </Container>

            {/* Add YobiAIWidget */}
            <YobiAIWidget />
          </Box>
        )}
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App; 